/* ═══════════════════════════════════════════════════════════════════════
   assets/quiz-result-vitalia.js

   Runtime personalization for the Vitalia result page.

   Reads URL params set by the quiz redirect (quiz-template-vitalia.js):
     - profile          → COMPOUND | ROUTE | SHIELD | GENERATE
     - overlay_primary  → e.g. 'bloodwork', 'three_am', 'aurivita_refugee'
     - overlay          → comma-separated full list
     - q_1 ... q_12     → handleized answer values
     - score_*          → bucket scores

   On page load:
     1. Reads overlay_primary, finds matching <section data-hero-variant="X">,
        hides the default hero, reveals the variant.
     2. Walks <section data-block-type="mirror-back"> blocks in DOM order,
        evaluates data-match-rule against q_* params, reveals the first
        match. If no match, reveals the block marked data-is-fallback or
        falls back to the first one.
     3. Replaces {Q10_PHRASE} in the future-pacing intro with a
        first-person phrase derived from q_12 (Q10's block index in the
        quiz template).
   ═══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ───── Q10 phrase map ─────
   * Maps the handleized Q10 answer value → first-person verb phrase that
   * fits "You said you want to {phrase}". Keys are the `text | handleize`
   * outputs of the Q10 answer strings.
   */
  var Q10_PHRASES = {
    'id-feel-like-myself-again':              'feel like yourself again',
    'id-stop-bracing-against-the-afternoon':  'stop bracing against your afternoons',
    'my-partner-would-notice-the-difference': 'have your partner notice the difference',
    'my-next-bloodwork-would-tell-a-different-story': 'have your next bloodwork tell a different story',
    'id-just-stop-thinking-about-it':         'stop thinking about this',
  };

  function init() {
    var root = document.querySelector('[data-result-section]');
    if (!root) return;

    var debug = root.dataset.debug === 'true';
    var log = debug
      ? function () { console.log.apply(console, ['[VitaliaResult]'].concat([].slice.call(arguments))); }
      : function () {};

    var params = new URLSearchParams(window.location.search);
    var profile = (params.get('profile') || root.dataset.profileDefault || 'GENERATE').toUpperCase();
    var overlayPrimary = params.get('overlay_primary') || '';
    var overlayAll = (params.get('overlay') || '').split(',').filter(Boolean);
    var qAnswers = {};
    for (var i = 1; i <= 14; i++) {
      var v = params.get('q_' + i);
      if (v) qAnswers[i] = v;
    }

    log('Page personalization input', {
      profile: profile,
      overlay_primary: overlayPrimary,
      overlay_all: overlayAll,
      answers: qAnswers,
    });

    // Set profile on root for any CSS scoping
    root.setAttribute('data-active-profile', profile);
    if (overlayPrimary) {
      root.setAttribute('data-active-overlay', overlayPrimary);
    }

    // Step 1 — hero overlay swap
    applyHeroVariant(root, overlayPrimary, log);

    // Step 2 — mirror-back rule matching
    applyMirrorBack(root, qAnswers, log);

    // Step 3 — Q10 splice into future-pacing intro
    applyQ10Splice(root, qAnswers, log);

    // Step 4 — bind Add-to-Cart buttons
    bindAddToCart(root, log);
  }

  /* ───── Step 4: Add to cart + checkout ───── */

  function bindAddToCart(root, log) {
    var buttons = root.querySelectorAll('[data-add-to-cart]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        handleAddToCart(btn, root, log);
      });
    });
  }

  function handleAddToCart(btn, root, log) {
    var variantId = btn.getAttribute('data-variant-id');
    var sellingPlanId = btn.getAttribute('data-selling-plan-id') || null;
    var tierName = btn.getAttribute('data-tier-name') || 'item';

    if (!variantId) {
      showToast(root, 'No variant configured for this tier.', 'error');
      return;
    }

    btn.classList.add('qr-btn--loading');
    var originalText = btn.textContent.trim();
    btn.setAttribute('data-original-text', originalText);

    var payload = {
      items: [{
        id: parseInt(variantId, 10),
        quantity: 1,
      }],
    };

    // Attach selling_plan for subscription tiers
    if (sellingPlanId) {
      payload.items[0].selling_plan = parseInt(sellingPlanId, 10);
    }

    // Attach attribution properties for tracking the quiz funnel source
    var params = new URLSearchParams(window.location.search);
    var props = {};
    var profile = params.get('profile');
    var overlay = params.get('overlay_primary');
    if (profile) props['_quiz_profile'] = profile;
    if (overlay) props['_quiz_overlay'] = overlay;
    if (Object.keys(props).length > 0) {
      payload.items[0].properties = props;
    }

    log('Add to cart payload', payload);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then(function (resp) {
        return resp.json().then(function (data) {
          return { ok: resp.ok, status: resp.status, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          var msg = (result.data && result.data.description)
            || (result.data && result.data.message)
            || 'Could not add to cart.';
          throw new Error(msg);
        }
        log('Add to cart success', result.data);
        // Forward UTM and quiz params to checkout
        var checkoutUrl = '/checkout';
        var forwardParams = new URLSearchParams();
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
          var v = params.get(k);
          if (v) forwardParams.set(k, v);
        });
        if (forwardParams.toString()) {
          checkoutUrl += '?' + forwardParams.toString();
        }
        window.location.href = checkoutUrl;
      })
      .catch(function (err) {
        log('Add to cart failed', err);
        btn.classList.remove('qr-btn--loading');
        btn.textContent = originalText;
        showToast(root, err.message || 'Something went wrong. Please try again.', 'error');
      });
  }

  var toastTimer = null;
  function showToast(root, message, type) {
    var toast = root.querySelector('[data-cart-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('qr-cart-toast--show');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove('qr-cart-toast--show');
    }, 4000);
  }

  /* ───── Step 1: Hero overlay swap ───── */

  function applyHeroVariant(root, overlayKey, log) {
    var defaultHero = root.querySelector('[data-block-type="hero"]');
    var variants = root.querySelectorAll('[data-block-type="hero-variant"]');

    if (!overlayKey) {
      log('No overlay → default hero shown');
      return;
    }

    // Find a variant matching the overlay
    var match = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].getAttribute('data-hero-variant') === overlayKey) {
        match = variants[i];
        break;
      }
    }

    if (!match) {
      log('Overlay "' + overlayKey + '" has no matching variant → default hero shown');
      return;
    }

    // Reveal variant, hide default
    match.classList.add('qr-hero--active');
    match.removeAttribute('data-block-hidden');
    if (defaultHero) {
      defaultHero.classList.add('qr-hero--hidden');
    }
    log('Hero variant activated:', overlayKey);
  }

  /* ───── Step 2: Mirror-back rule matching ───── */

  function applyMirrorBack(root, qAnswers, log) {
    var blocks = Array.prototype.slice.call(root.querySelectorAll('[data-block-type="mirror-back"]'));
    if (blocks.length === 0) {
      log('No mirror-back blocks present');
      return;
    }

    // Walk blocks in DOM order, find first match
    var matched = null;
    var fallback = null;
    for (var i = 0; i < blocks.length; i++) {
      var b = blocks[i];
      if (b.getAttribute('data-is-fallback') === 'true' && !fallback) {
        fallback = b;
      }
      var rule = b.getAttribute('data-match-rule') || '';
      if (!rule) continue;
      if (evaluateMatchRule(rule, qAnswers)) {
        matched = b;
        break;
      }
    }

    // Fallback hierarchy: matched → marked fallback → first block
    var winner = matched || fallback || blocks[0];
    winner.classList.add('qr-mirror--active');
    winner.removeAttribute('data-block-hidden');
    log('Mirror-back winner', {
      rule: winner.getAttribute('data-match-rule') || '(fallback)',
      via: matched ? 'rule-match' : (fallback ? 'fallback-flag' : 'first-block'),
    });
  }

  /**
   * Evaluate a comma-separated list of conditions against qAnswers.
   * Format: "q3:contains:cold-hands,q6:contains:nothing-yet"
   * Operators supported:
   *   - contains  → the question's value (possibly comma-separated for
   *                 multi-select) contains this token as a substring
   *   - equals    → exact match
   *   - has       → token appears in the comma-separated value list
   * ALL conditions must match for the rule to pass.
   */
  function evaluateMatchRule(rule, qAnswers) {
    var conditions = rule.split(',').map(function (c) { return c.trim(); }).filter(Boolean);
    if (conditions.length === 0) return false;

    for (var i = 0; i < conditions.length; i++) {
      var parts = conditions[i].split(':');
      if (parts.length < 3) continue;
      var qKey = parts[0].toLowerCase();             // "q3"
      var op = parts[1].toLowerCase();               // "contains" | "equals" | "has"
      var target = parts.slice(2).join(':').trim();  // "cold-hands"

      // Map "q3" → block index. Quiz block indexes for the question slots
      // are NOT 1:1 with question numbers because interstitials/loading
      // blocks live between them. We use a lookup table mapping Q-number
      // → block index per the quiz template's block_order. This is
      // hardcoded here because the template structure is stable.
      var qNum = parseInt(qKey.replace('q', ''), 10);
      var blockIdx = QUESTION_TO_BLOCK_IDX[qNum];
      if (!blockIdx) return false;

      var val = qAnswers[blockIdx];
      if (!val) return false;

      var pass = false;
      if (op === 'contains') {
        pass = val.indexOf(target) !== -1;
      } else if (op === 'equals') {
        pass = val === target;
      } else if (op === 'has') {
        // For multi-select answers stored as "a,b,c"
        var tokens = val.split(',').map(function (s) { return s.trim(); });
        pass = tokens.indexOf(target) !== -1;
      }
      if (!pass) return false;
    }
    return true;
  }

  /**
   * Quiz block index → question number mapping.
   * Block order in the quiz template:
   *   1: q1_age       → Q1
   *   2: q2_buyer     → Q2
   *   3: q3_symptoms  → Q3
   *   4: q4_duration  → Q4
   *   5: q5_trigger   → Q5
   *   6: i1_mechanism (interstitial)
   *   7: q6_tried     → Q6
   *   8: q7_outcome   → Q7
   *   9: i2_social_proof (interstitial)
   *  10: q8_pattern   → Q8
   *  11: q9_fear      → Q9
   *  12: q10_identity → Q10
   *  13: loading_screen
   *  14: final_cta
   */
  var QUESTION_TO_BLOCK_IDX = {
    1: 1,  2: 2,  3: 3,  4: 4,  5: 5,
    6: 7,  7: 8,
    8: 10, 9: 11, 10: 12,
  };

  /* ───── Step 3: Q10 splice into future-pacing intro ───── */

  function applyQ10Splice(root, qAnswers, log) {
    var futureBlock = root.querySelector('[data-block-type="future-pacing"]');
    if (!futureBlock) return;
    var target = futureBlock.querySelector('[data-q10-target]');
    if (!target) return;

    var template = futureBlock.getAttribute('data-q10-template') || '';
    if (template.indexOf('{Q10_PHRASE}') === -1) {
      log('Future-pacing template has no {Q10_PHRASE} placeholder; skipping splice');
      return;
    }

    var q10Value = qAnswers[QUESTION_TO_BLOCK_IDX[10]] || '';
    var phrase = Q10_PHRASES[q10Value];

    if (!phrase) {
      // Fallback: use the root-level fallback string
      phrase = root.dataset.fallbackQ10 || 'feel like yourself again';
      log('Q10 value "' + q10Value + '" not in phrase map; using fallback "' + phrase + '"');
    } else {
      log('Q10 phrase resolved:', phrase);
    }

    var rendered = template.replace('{Q10_PHRASE}', phrase);
    target.textContent = rendered;
  }

  /* ───── Boot ───── */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());